import {
  createAsyncThunk,
  createEntityAdapter,
  createSlice,
} from '@reduxjs/toolkit';

import { RootState } from '../../../../app/store/configureStore';
import { RestaurantTable, TableStatus } from '../types';
import api from '../../../../app/api/api';
import { handleApiError } from '../../../../app/api/errorHandler';
import i18n from '../../../../i18n';

export const fetchRestaurantTableListAsync = createAsyncThunk(
  'table/fetchRestaurantTableList',
  async (restaurantId: string, thunkApi) => {
    try {
      return await api.RestaurantTable.list(restaurantId);
    } catch (error: unknown) {
      handleApiError(error, i18n.t('common.error'));
      if (error && typeof error === 'object' && 'data' in error) {
        return thunkApi.rejectWithValue({
          error: (error as { data: unknown }).data,
        });
      }
      return thunkApi.rejectWithValue({ error: 'An unknown error occurred' });
    }
  }
);

export const createTableAsync = createAsyncThunk(
  'table/createTable',
  async (
    tableData: {
      restaurantId: string;
      tableNumber: number;
      capacity: number;
      notes: string;
      status: TableStatus;
    },
    thunkApi
  ) => {
    try {
      return await api.RestaurantTable.create(
        tableData.restaurantId,
        tableData
      );
    } catch (error: unknown) {
      if (error && typeof error === 'object' && 'data' in error) {
        return thunkApi.rejectWithValue({
          error: (error as { data: unknown }).data,
        });
      }
      return thunkApi.rejectWithValue({ error: 'An unknown error occurred' });
    }
  }
);

export const updateTableAsync = createAsyncThunk(
  'table/updateTable',
  async (
    updateData: {
      restaurantId: string;
      tableId: string;
      tableNumber: number;
      capacity: number;
      notes: string;
      status: TableStatus;
    },
    thunkApi
  ) => {
    try {
      return await api.RestaurantTable.update(
        updateData.restaurantId,
        updateData.tableId,
        updateData
      );
    } catch (error: unknown) {
      handleApiError(error, i18n.t('common.error'));
      if (error && typeof error === 'object' && 'data' in error) {
        return thunkApi.rejectWithValue({
          error: (error as { data: unknown }).data,
        });
      }
      return thunkApi.rejectWithValue({ error: 'An unknown error occurred' });
    }
  }
);

export const updateTableStatusAsync = createAsyncThunk(
  'table/updateTableStatus',
  async (
    {
      restaurantId,
      tableId,
      status,
    }: {
      restaurantId: string;
      tableId: string;
      status: TableStatus;
    },
    thunkApi
  ) => {
    try {
      return await api.RestaurantTable.updateStatus(
        restaurantId,
        tableId,
        status
      );
    } catch (error: unknown) {
      if (error && typeof error === 'object' && 'data' in error) {
        return thunkApi.rejectWithValue({
          error: (error as { data: unknown }).data,
        });
      }
      return thunkApi.rejectWithValue({ error: 'An unknown error occurred' });
    }
  }
);

export const deleteTableAsync = createAsyncThunk(
  'table/deleteTable',
  async (
    {
      restaurantId,
      tableId,
    }: {
      restaurantId: string;
      tableId: string;
    },
    thunkApi
  ) => {
    try {
      await api.RestaurantTable.delete(restaurantId, tableId);
      return tableId;
    } catch (error: unknown) {
      handleApiError(error, i18n.t('common.error'));
      if (error && typeof error === 'object' && 'data' in error) {
        return thunkApi.rejectWithValue({
          error: (error as { data: unknown }).data,
        });
      }
      return thunkApi.rejectWithValue({ error: 'An unknown error occurred' });
    }
  }
);

const tablesAdapter = createEntityAdapter<RestaurantTable>();

export const tableSlice = createSlice({
  name: 'table',
  initialState: tablesAdapter.getInitialState({
    status: 'idle',
    selectedTable: null as RestaurantTable | null,
    qrCode: null as string | null,
  }),
  reducers: {
    setSelectedTable: (state, action) => {
      state.selectedTable = action.payload;
    },
    clearSelectedTable: (state) => {
      state.selectedTable = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch tables
      .addCase(fetchRestaurantTableListAsync.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchRestaurantTableListAsync.fulfilled, (state, action) => {
        tablesAdapter.setAll(state, action.payload);
        state.status = 'idle';
      })
      .addCase(fetchRestaurantTableListAsync.rejected, (state) => {
        state.status = 'idle';
      })
      // Create table
      .addCase(createTableAsync.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(createTableAsync.fulfilled, (state, action) => {
        tablesAdapter.addOne(state, action.payload);
        state.status = 'idle';
      })
      .addCase(createTableAsync.rejected, (state) => {
        state.status = 'idle';
      })
      // Update table
      .addCase(updateTableAsync.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(updateTableAsync.fulfilled, (state, action) => {
        tablesAdapter.upsertOne(state, action.payload);
        state.status = 'idle';
      })
      .addCase(updateTableAsync.rejected, (state) => {
        state.status = 'idle';
      })
      // Update table status
      .addCase(updateTableStatusAsync.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(updateTableStatusAsync.fulfilled, (state, action) => {
        tablesAdapter.upsertOne(state, action.payload);
        state.status = 'idle';
      })
      .addCase(updateTableStatusAsync.rejected, (state) => {
        state.status = 'idle';
      })
      // Delete table
      .addCase(deleteTableAsync.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(deleteTableAsync.fulfilled, (state, action) => {
        tablesAdapter.removeOne(state, action.payload);
        state.status = 'idle';
      })
      .addCase(deleteTableAsync.rejected, (state) => {
        state.status = 'idle';
      });
  },
});

export const { setSelectedTable, clearSelectedTable } = tableSlice.actions;

// Export the selectors
export const tableSelectors = tablesAdapter.getSelectors<RootState>(
  (state) => state.tables
);

export default tableSlice.reducer;
